import {
  $applyNodeReplacement,
  $getState,
  $getStateChange,
  $setState,
  ParagraphNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type RangeSelection,
  type SerializedParagraphNode,
} from 'lexical';

import { templateLineState } from './templateNodeState';

const markLine = (dom: HTMLElement, node: TemplateParagraphNode) => {
  const line = $getState(node, templateLineState, 'direct');
  if (line) {
    dom.dataset.lineId = line.ID;
  } else {
    delete dom.dataset.lineId;
  }
  dom.toggleAttribute('data-linked', !!line?.ShowWhen.length);
  dom.toggleAttribute('data-subheading', !!line?.Subheading);
  return dom;
};

const isAtStart = (selection: RangeSelection, node: TemplateParagraphNode) => {
  const { anchor } = selection;
  const at = anchor.getNode();
  return (
    anchor.offset === 0 && (at.is(node) || at.is(node.getFirstDescendant()))
  );
};

// A line of the template: one paragraph, which carries the line's id and
// links.
export class TemplateParagraphNode extends ParagraphNode {
  static getType(): string {
    return 'template-paragraph';
  }

  static clone(node: TemplateParagraphNode): TemplateParagraphNode {
    return new TemplateParagraphNode(node.__key);
  }

  static importJSON(json: SerializedParagraphNode): TemplateParagraphNode {
    return new TemplateParagraphNode().updateFromJSON(json);
  }

  constructor(key?: NodeKey) {
    super(key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    return markLine(super.createDOM(config), this);
  }

  updateDOM(
    previous: TemplateParagraphNode,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    if ($getStateChange(this, previous, templateLineState)) {
      markLine(dom, this);
    }
    return super.updateDOM(previous, dom, config);
  }

  // Enter at the start of a line moves its content into a new paragraph
  // after it: the line's id, links and subheading go with the content, and
  // the empty paragraph left before it is a new line.
  insertNewAfter(
    selection: RangeSelection,
    restoreSelection: boolean,
  ): ParagraphNode {
    const moves = !this.isEmpty() && isAtStart(selection, this);
    const line = $getState(this, templateLineState);
    const next = super.insertNewAfter(selection, restoreSelection);
    if (moves) {
      $setState(next, templateLineState, line);
      $setState(this, templateLineState, null);
    }
    return next;
  }
}

export const $createTemplateParagraphNode = () =>
  $applyNodeReplacement(new TemplateParagraphNode());

export const $isTemplateParagraphNode = (
  node: LexicalNode | null | undefined,
): node is TemplateParagraphNode => node instanceof TemplateParagraphNode;
